import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { hlp_getContentDate } from '@app/_v3/helpers/content-helpers';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { BookmarkComponent } from '@core/components/bookmark/bookmark.component';
import { videoType, vimeoCoverImageUrl, youtubeCoverImageUrl } from '@core/helpers/video';
import { PipesModule } from '@core/pipes/pipes.module';
import { AccountService, ContentService, FilesService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { IconComponent } from '@shared/icon/icon.component';
import { RatingComponent } from '@shared/rating/rating.component';
import { getLanguages, getSubjects } from '@store/reducers/data.reducer';
import {
  Content,
  ContentTypeEnum,
  IAccountStats,
  ImageSizeEnum,
  LessonContentType,
  SubjectCategoryEnum,
  User,
} from 'lingo2-models';
import { Router as ContentRouter } from 'lingo2-models/dist/content/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { i18nService as TranslateService } from '../../../../../i18n/translate.service';

@Component({
  selector: 'app-lesson',
  standalone: true,
  imports: [
    LocaleSharedModule,
    BookmarkComponent,
    CommonModule,
    PipesModule,
    OnUiCoverModule,
    RatingComponent,
    IconComponent,
  ],
  templateUrl: './lesson.component.html',
  styleUrls: ['./lesson.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LessonComponent extends ChangableComponent implements OnInit {
  private translate = inject(TranslateService);
  private accountService = inject(AccountService);
  private contentService = inject(ContentService);
  private store = inject(Store);
  deviceService = inject(DeviceDetectorService);

  @Input() view: string;

  @Input()
  public set content(content: Content | Partial<Content>) {
    this._content = content;
    this.prepareContentCard();
  }

  public get content() {
    return this._content;
  }

  @Output() public openInModal = new EventEmitter<Content | Partial<Content>>();

  public accountRoute = AccountService.accountRoute;
  public contentRoute = LibraryRouter.contentRouteUniversal;
  public hasCoverImage = true;
  public author: User;
  public authorName: string;
  public authorFullName: string;
  public subject: string;
  public level: string;
  public contentUrl: string;
  public contentTypeTitle: string;
  public contentTypeUrl = '';
  public contentDate: Date;
  public userpicUrl: string;
  public coverImageUrl: string;
  public teachingLanguage: string;
  public authorAvatar: OnUiCover;
  public authorStats: Partial<IAccountStats>;
  public cover: OnUiCover;
  public metaImageUrl: string;
  public fullTitle: string;
  public cutLength = 12;
  public subjectCategoryEnum = SubjectCategoryEnum;
  public description: string;
  public subscribed = false;
  public doHover = false;
  private _content: Content | Partial<Content>;

  ngOnInit(): void {
    this.contentService.change$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (res) => {
        if (res && res.id === this.content.id) {
          this.content.stats = { ...this.content.stats, ...res.stats };
          this.detectChanges();
        }
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    combineLatest([this.store.select(getSubjects), this.store.select(getLanguages)])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([subjects, languages]) => {
          this.subject = subjects.find((el) => el.id == (this.content.subject_id || this.content.subject?.id))?.title;
          this.teachingLanguage = languages.all.find(
            (el) => el.id == (this.content.language_id || this.content.language?.id),
          )?.title;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get isImage(): boolean {
    return this.content.type === ContentTypeEnum.image;
  }

  public get isLesson(): boolean {
    return LessonContentType.includes(this.content.type);
  }

  public get isVideo(): boolean {
    return this.content.type === ContentTypeEnum.video;
  }

  public get isAudio(): boolean {
    return this.content.type === ContentTypeEnum.audio;
  }

  public get isMediaTypeNecessary() {
    return this.isVideo || this.isAudio || this.isImage;
  }

  protected prepareContentCard() {
    this.fullTitle = this.content.title;
    if (!!this.content.level_other_name) {
      this.level = this.content.level_other_name;
    } else {
      this.level =
        'level' in this.content && this.content.level
          ? this.content.level.title
          : this.translate.directInstant('subject-levels.any-level');
    }
    this.contentTypeTitle = ContentService.contentTypeTitle[this.content.type];
    this.contentTypeUrl = ContentRouter.getNameByContentType(this.content.type);
    this.contentDate = hlp_getContentDate(this.content?.published_at, this.content?.created_at);
    this.contentUrl = this.content ? this.contentRoute(this.content) : null;
    this.description = this.content?.sub_title;

    this.prepareCoverImage();
    this.cover = {
      type: 'cover',
      img: this.coverImageUrl,
      title: this.fullTitle,
      link: this.contentUrl,
      form: 'base',
    };
    this.prepareMetaImage();
    this.markForCheck();

    if (this.content.author) {
      this.prepareContentCardByAuthor(this.content.author);
    }
  }

  protected prepareContentCardByAuthor(user: User) {
    this.author = user;
    this.authorName = AccountService.getUserName(this.author);
    this.authorFullName = AccountService.getUserFullName(this.author);
    this.userpicUrl =
      this.prepareStorageUrl(this.content.author?.__opts?.images[0]?.sm) ||
      this.prepareStorageUrl(this.content.author?.__opts?.images[0]?.md) ||
      FilesService.getFileUrlBySize(this.author.userpic_id, ImageSizeEnum.sm);
    this.authorStats = this.author.stats;
    this.authorAvatar = {
      type: 'avatar',
      img: this.userpicUrl,
      title: this.authorFullName,
      link: this.accountRoute(this.author).join('/'),
      form: 'circle',
    };
    this.detectChanges();
  }

  protected prepareCoverImage() {
    if (!this.content.cover && this.isVideo) {
      const _video = this.content.media_url;
      const _type = videoType(_video);
      switch (_type) {
        case 'youtube':
          this.coverImageUrl = youtubeCoverImageUrl(_video);
          break;

        case 'vimeo':
          this.coverImageUrl = vimeoCoverImageUrl(_video);
          break;
      }
      return;
    }
    this.coverImageUrl = this.content?.cover?.md
      ? this.prepareStorageUrl(this.content?.cover?.md?.url)
      : FilesService.getFileUrlBySize(this.content?.cover_id);
  }

  protected prepareMetaImage() {
    if (this.isVideo) {
      this.metaImageUrl = this.coverImageUrl;
    } else {
      this.metaImageUrl = this.content.__opts?.images[0]?.md
        ? this.prepareStorageUrl(this.content.__opts?.images[0]?.md)
        : FilesService.getFileBySize(this.content?.cover, ImageSizeEnum.lg)?.url ||
          FilesService.getFileUrlBySize(this.content?.cover_id, ImageSizeEnum.lg);
    }
  }

  protected loadIsSubscribed() {
    if (!this.content) {
      return;
    }
    this.accountService
      .getIsSubscribed(this.content.author_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.subscribed = result;
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.markForCheck();
        },
      });
  }

  public onToggleSubscribe(event) {
    event.preventDefault();
    event.cancelBubble = true;
    this.markForCheck();
    if (this.subscribed) {
      this.accountService
        .unsubscribe(this.content.author_id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.subscribed = false;
            this.markForCheck();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            this.markForCheck();
          },
        });
    } else {
      this.accountService
        .subscribe(this.content.author_id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.subscribed = true;
            this.markForCheck();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            this.markForCheck();
          },
        });
    }
  }

  public doHoverImage() {
    this.doHover = !this.doHover;
  }

  public onOpenCardInModal() {
    this.openInModal.emit(this.content);
  }

  public favoriteChanged(event) {
    this._content.visit_info.is_favorite = event;
  }

  public get doBlur() {
    return this.view === 'vertical' || this.view === 'vertical-square' || this.view === 'wide-vertical';
  }
}
